import { useQuery } from '@tanstack/react-query';
import type {
  RulesetSummaryDto,
  RulesetSource,
  MdExpenseCategoryGroupDto,
  StatutoryElectionDatesDto,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, apiResponse } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { FINANCE_STALE_TIME } from './queryUtils';

export function useRulesets() {
  return useQuery({
    queryKey: queryKeys.finance.rulesets.list(),
    queryFn: () =>
      apiJson<RulesetSummaryDto[]>('/api/finance/rulesets', { error: 'Failed to fetch rulesets' }),
    enabled: authService.isAuthenticated(),
  });
}

/** The election dates the jurisdiction's law fixes for `year`, offered for a blank date. */
export function useStatutoryElectionDates(code: string | null, year: number | null) {
  return useQuery({
    queryKey: queryKeys.finance.rulesets.statutoryElectionDates(code ?? '', year ?? 0),
    queryFn: () =>
      apiJson<StatutoryElectionDatesDto>(
        `/api/finance/rulesets/${encodeURIComponent(code ?? '')}/statutory-election-dates?year=${year}`,
        { error: 'Failed to fetch statutory election dates' },
      ),
    enabled: authService.isAuthenticated() && !!code && year !== null,
    staleTime: Infinity,
  });
}

export interface RulesetSourceResult {
  /** The file text exactly as served — what the raw view shows. */
  text: string;
  ruleset: RulesetSource;
}

/**
 * The ruleset FILE for the rules viewer — every version, every policy switch, and the
 * `_comment` citations the typed detail endpoint drops. Keeps the served text alongside
 * the parsed shape so the raw view and the sections come from one fetch. Keyed by ruleset
 * code (the file is global reference data), but the endpoint requires Finance.Manage in the
 * current campaign because the notes cite internal issues. Resolves null for an unknown code.
 */
export function useRulesetSource(code: string | null) {
  return useQuery({
    queryKey: queryKeys.finance.rulesets.source(code ?? ''),
    queryFn: async (): Promise<RulesetSourceResult | null> => {
      const response = await apiResponse(`/api/finance/rulesets/${code}/source`);
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Failed to fetch ruleset source');
      const text = await response.text();
      return { text, ruleset: JSON.parse(text) as RulesetSource };
    },
    enabled: authService.isAuthenticated() && !!code,
    staleTime: FINANCE_STALE_TIME,
  });
}

/**
 * Maryland (MDCRIS, ADR-073) — the coded Expense Category → Purpose cascade driving the searchable,
 * category-filtered Purpose picker on the expenditure form. IS the checked-in
 * `MdExpensePurposeAllowList` (served, never re-transcribed on the client), so the picker can only
 * offer listed pairs. Only fetched for a Maryland ruleset; the endpoint returns [] for any other code,
 * so a non-MD form renders no picker. Global reference data — keyed by ruleset code, not campaign.
 */
export function useMdExpensePurposeCascade(rulesetCode: string | null | undefined) {
  const isMaryland = (rulesetCode ?? '').toUpperCase() === 'MD';
  return useQuery({
    queryKey: queryKeys.finance.rulesets.expensePurposes(rulesetCode ?? ''),
    queryFn: () =>
      apiJson<MdExpenseCategoryGroupDto[]>(`/api/finance/rulesets/${rulesetCode}/expense-purposes`, {
        error: 'Failed to fetch Maryland expense purposes',
      }),
    enabled: authService.isAuthenticated() && isMaryland,
    staleTime: FINANCE_STALE_TIME,
  });
}
